import {observer} from 'mobx-react';
import React from 'react';
import {X3Level} from '../../../domain';
import {
  DisplayError,
  Link,
  PageHeading,
  PromiseMessage,
  PromiseRetry
} from '../../../lib/components';
import {ReactNodeGenerator} from '../../../lib/helpers';
import {routes} from '../../../pages/Routes';
import {AddLevelControls} from './parts/AddLevelControls';
import {AddLevelForm} from './parts/AddLevelForm';
import {DeleteLevelControls} from './parts/DeleteLevelControls';
import {EditLevelControls} from './parts/EditLevelControls';
import {EditLevelForm} from './parts/EditLevelForm';
import {MoveLevelControls} from './parts/MoveLevelControls';
import {MoveLevelForm} from './parts/MoveLevelForm';
import {ReorderLevelsControls} from './parts/ReorderLevelsControls';
import {X2AgeCategoryBreadcrumbs} from './X2AgeCategoryBreadcrumbs';
import {X2AgeCategoryStore} from './X2AgeCategoryStore';
import {userStatusService} from '../../../domain'

function LevelViewNoActions(level: X3Level): React.ReactNode {
  return (
    <div className="row" key={'level-' + level.Id}>
      <div className="col">
        <div className="text-box">
          {level.Name}
        </div>
      </div>
    </div>
  );
}

function* ViewModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const levels = store.loader.levels;
  if (levels.length === 0)
    yield (
      <div className="row justify-content-center" key="no-levels">
        <div className="col-auto">
          <div className="alert alert-info">
            No levels
          </div>
        </div>
      </div>);

  for (const level of levels) {
    yield (
      <div className="row" key={'level-' + level.Id}>
        <div className="col">
          <div className="text-box">
            {level.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <Link className="btn btn-primary" to={routes.x3level(level.Id)}> View </Link>
          {
            userStatusService.status.roles.ContentManager &&
            <>
                <button type="button" className="btn btn-primary"
                        onClick={() => store.beginEdit(level.Id)}> Edit
                </button>
                <button type="button" className="btn btn-primary"
                        onClick={() => store.beginMove(level.Id)}> Move
                </button>
                <button type="button" className="btn btn-danger"
                        onClick={() => store.beginDelete(level.Id)}> Delete
                </button>
            </>
          }
        </div>
      </div>);
  }
}

function* AddModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const levels = store.loader.levels;
  for (const level of levels)
    yield LevelViewNoActions(level);

  yield (<AddLevelForm key="add-level-form"
                       store={store}/>);

  yield (<AddLevelControls key="add-level-controls"
                           store={store}/>);
}

function* EditModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const levels = store.loader.levels;
  const editLevelId = store.editLevel!.level.Id;

  for (const level of levels) {
    if (level.Id !== editLevelId) {
      yield LevelViewNoActions(level);
      continue;
    }

    yield (<EditLevelForm key="edit-level-form" store={store}/>);

    yield (<EditLevelControls key="edit-level-controls" store={store}/>);
  }
}

function* MoveModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const levels = store.loader.levels;
  const moveLevelId = store.move!.level.Id;

  for (const level of levels) {
    yield LevelViewNoActions(level);

    if (level.Id !== moveLevelId)
      continue;

    yield (<MoveLevelForm key="move-level-form" store={store}/>);

    yield (<MoveLevelControls key="move-level-controls" store={store}/>);
  }
}

function* DeleteModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const levels = store.loader.levels;
  const deleteLevelId = store.deleteLevel!.levelId;

  for (let level of levels) {
    yield LevelViewNoActions(level);

    if (level.Id !== deleteLevelId)
      continue;

    yield (<DeleteLevelControls key="delete-level-controls" store={store}/>);
  }
}

function* ReorderModeItems(store: X2AgeCategoryStore): ReactNodeGenerator {
  const reorder = store.reorder!;
  const levelById = store.loader.levelById;

  const levelIds = reorder.levelIds;

  for (let index = 0; index < levelIds.length; index++) {
    let levelId = levelIds[index];
    const level = levelById[levelId];
    const first = index === 0;
    const last = index === levelIds.length - 1;

    yield (
      <div className="row" key={'level-' + level.Id}>
        <div className="col">
          <div className="text-box">
            {level.Name}
          </div>
        </div>
        <div className="col-auto no-wrap">
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderUp(index)}
                  disabled={first}>
            <span className="fas fa-arrow-up"/>
          </button>
          <button type="button" className="btn btn-sm"
                  onClick={() => store.reorderDown(index)}
                  disabled={last}>
            <span className="fas fa-arrow-down"/>
          </button>
        </div>
      </div>);
  }

  yield (<ReorderLevelsControls key="reorder-levels-controls"
                                store={store}/>);
}

function* cardBodyRenderer(store: X2AgeCategoryStore): ReactNodeGenerator {
  const mode = store.mode;

  switch (mode) {
    case 'View':
      yield* ViewModeItems(store);
      break;
    case 'Edit':
      yield* EditModeItems(store);
      break;
    case 'Add':
      yield* AddModeItems(store);
      break;
    case 'Delete':
      yield* DeleteModeItems(store);
      break;
    case 'ReOrder':
      yield* ReorderModeItems(store);
      break;
    case 'Move':
      yield* MoveModeItems(store);
      break;
  }
}

function* componentRenderer(store: X2AgeCategoryStore): ReactNodeGenerator {
  const loaderState = store.loader.state;
  const isPending = loaderState?.isPending === true;
  const isFailure = loaderState?.isRejected === true;
  const isSuccess = loaderState?.isResolved === true;
  const ageCategory = store.loader.ageCategory;

  if (isPending || isFailure) {
    yield (<PageHeading key="heading"> Age Category </PageHeading>);

    yield (<PromiseMessage key="lookup-loader"
                           label="Loading age category"
                           state={loaderState}
                           align="right"/>);

    yield (<PromiseRetry key="lookup-retry"
                         state={loaderState}
                         retry={store.loader.load}
                         align="right"/>);

    return;
  }

  if (!isSuccess)
    return;

  if (ageCategory === null) {
    yield (<DisplayError key="age-category-not-found">
      Age category with Id {store.ageCategoryId} not found
    </DisplayError>);
    return;
  }

  yield (<X2AgeCategoryBreadcrumbs key="age-category-breadcrumbs"
                                   ageCategory={ageCategory}/>);

  yield (<PageHeading key="heading"> Age category {ageCategory.Name} </PageHeading>);

  yield (
    <div className="row" key="main">
      <div className="col-12">
        <div className="card">
          <div className="card-header">
            <div className="row align-items-center">
              <div className="col"> Levels</div>
              {
                store.mode === 'View' &&
                <div className="col-auto no-wrap">
                  {
                    userStatusService.status.roles.ContentManager &&
                    <>
                      <button type="button" className="btn btn-primary"
                              onClick={store.beginAdd}> Add
                      </button>
                      <button type="button" className="btn btn-primary"
                              disabled={ageCategory.Levels!.length < 2}
                              onClick={store.beginReorder}> Reorder
                      </button>
                    </>
                  }
                </div>
              }
            </div>
          </div>

          <div className="card-body">
            {Array.from(cardBodyRenderer(store))}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  id: number;
}

@observer
export class X2AgeCategoryComponent extends React.Component<Props> {
  readonly store: X2AgeCategoryStore;

  constructor(props: Readonly<Props>) {
    super(props);
    this.store = new X2AgeCategoryStore(props.id);
  }

  componentDidMount() {
    this.store.loader.load();
  }

  render() {
    return Array.from(componentRenderer(this.store));
  }

  componentWillUnmount(): void {
    this.store.dispose();
  }
}
